import { useEffect, useRef, useState } from 'react';
import { Contact } from '../types';
import { formatPhoneNumber } from '../utils/phoneUtils';
import { maskCPF } from '../utils/documentUtils';

interface ContactListProps {
  contacts: Contact[];
  loading: boolean;
  onEdit: (contact: Contact) => void;
  onDelete: (id: string) => void;
  selectedContactIds: string[];
  onToggleContactSelection: (id: string) => void;
  onToggleAllContacts: () => void;
}

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });

const getCategoryStyle = (hexColor?: string | null) => {
  if (!hexColor) {
    return {
      color: '#1e3a5f',
      backgroundColor: 'rgba(30, 58, 95, 0.12)',
    };
  }

  return {
    color: hexColor,
    backgroundColor: `${hexColor}22`,
  };
};

export function ContactList({
  contacts,
  loading,
  onEdit,
  onDelete,
  selectedContactIds,
  onToggleContactSelection,
  onToggleAllContacts,
}: ContactListProps) {
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const selectAllRef = useRef<HTMLInputElement>(null);

  const allSelected = contacts.length > 0 && selectedContactIds.length === contacts.length;
  const someSelected = selectedContactIds.length > 0 && !allSelected;

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someSelected;
    }
  }, [someSelected]);

  const handleDelete = (id: string) => {
    if (deleteConfirm === id) {
      onDelete(id);
      setDeleteConfirm(null);
      return;
    }

    setDeleteConfirm(id);
  };

  const skeletonRows = Array.from({ length: 8 });

  const renderCategory = (contact: Contact) => {
    if (!contact.categoria) {
      return <span className="text-xs text-slate-400">Sem categoria</span>;
    }

    return (
      <span
        className="inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium"
        style={getCategoryStyle(contact.categoria.cor)}
      >
        {contact.categoria.nome}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="space-y-3 p-4" role="status" aria-label="Carregando destinatários">
        {skeletonRows.map((_, index) => (
          <div key={index} className="h-14 animate-pulse rounded-xl bg-slate-100" />
        ))}
      </div>
    );
  }

  if (contacts.length === 0) {
    return (
      <div className="py-12 text-center text-gray-500">
        <p>Nenhum destinatário encontrado.</p>
      </div>
    );
  }

  return (
    <>
      <div className="hidden overflow-x-auto md:block">
        <table className="min-w-full bg-white" role="table">
          <thead className="border-b border-slate-200 bg-slate-50/90">
            <tr>
              <th className="w-12 px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">
                <input
                  type="checkbox"
                  ref={selectAllRef}
                  className="h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-600"
                  checked={allSelected}
                  onChange={onToggleAllContacts}
                  aria-label="Selecionar todos os destinatários desta página"
                />
              </th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Nome</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Telefone</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Categoria</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Info adicional</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-slate-500">Última atualização</th>
              <th className="px-4 py-3 text-right text-xs font-medium uppercase tracking-wider text-slate-500">Ações</th>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {contacts.map((contact, index) => (
              <tr
                key={contact.id}
                className={`align-top transition-colors hover:bg-blue-50/40 ${index % 2 === 0 ? 'bg-white' : 'bg-slate-50/40'}`}
              >
                <td className="px-4 py-4">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-600"
                    checked={selectedContactIds.includes(contact.id)}
                    onChange={() => onToggleContactSelection(contact.id)}
                    aria-label={`Selecionar destinatário ${contact.nome}`}
                  />
                </td>

                <td className="px-4 py-4 text-sm text-slate-700">
                  <div className="text-sm font-semibold text-slate-900">{contact.nome}</div>
                  <div className="mt-1 text-xs text-slate-500">
                    {contact.email ? contact.email : 'Sem e-mail'}
                    {contact.document ? ` • CPF ${maskCPF(contact.document)}` : ''}
                  </div>
                </td>

                <td className="px-4 py-4 text-sm text-slate-600">
                  <div className="text-sm text-slate-700">{formatPhoneNumber(contact.telefone)}</div>
                </td>

                <td className="px-4 py-4 text-sm text-slate-700">
                  {renderCategory(contact)}
                </td>

                <td className="px-4 py-4 text-sm text-slate-700">
                  <div className="line-clamp-1 text-sm text-slate-700">{contact.endereco || 'Endereço não informado'}</div>
                  <div className="mt-1 line-clamp-1 text-xs text-slate-500">
                    Processo: {contact.processo || '-'} • Mandado: {contact.mandado || '-'}
                  </div>
                </td>

                <td className="px-4 py-4 text-sm text-slate-700">
                  <div className="text-sm">{formatDate(contact.atualizadoEm)}</div>
                  <div className="mt-1 line-clamp-1 text-xs text-slate-500">{contact.observacoes || 'Sem observações'}</div>
                </td>

                <td className="px-4 py-4 text-sm font-medium">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => onEdit(contact)}
                      className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-200"
                      aria-label={`Editar destinatário ${contact.nome}`}
                      title="Editar"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="m4 20 4.2-.8L19 8.4l-3.4-3.4L4.8 15.8 4 20Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                      </svg>
                    </button>

                    <button
                      onClick={() => handleDelete(contact.id)}
                      className={`rounded-lg p-2 transition focus:outline-none focus:ring-2 focus:ring-red-100 ${
                        deleteConfirm === contact.id
                          ? 'bg-red-100 text-red-700'
                          : 'text-red-600 hover:bg-red-50'
                      }`}
                      aria-label={
                        deleteConfirm === contact.id
                          ? `Confirmar exclusão de ${contact.nome}`
                          : `Excluir destinatário ${contact.nome}`
                      }
                      title={deleteConfirm === contact.id ? 'Confirmar exclusão' : 'Excluir'}
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                        <path d="M4 7h16M10 11v6m4-6v6M7 7l1 12h8l1-12M9 7l1-2h4l1 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="space-y-3 p-3 md:hidden">
        {contacts.map((contact) => (
          <article key={contact.id} className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm transition hover:border-slate-300">
            <div className="flex items-start justify-between gap-3">
              <label className="flex items-start gap-3">
                <input
                  type="checkbox"
                  className="mt-1 h-4 w-4 rounded border-slate-300 text-blue-700 focus:ring-blue-600"
                  checked={selectedContactIds.includes(contact.id)}
                  onChange={() => onToggleContactSelection(contact.id)}
                  aria-label={`Selecionar destinatário ${contact.nome}`}
                />
                <div>
                  <p className="text-sm font-semibold text-slate-900">{contact.nome}</p>
                  <p className="mt-1 text-xs text-slate-500">{formatPhoneNumber(contact.telefone)}</p>
                </div>
              </label>

              <div className="flex items-center gap-1">
                <button
                  onClick={() => onEdit(contact)}
                  className="rounded-lg p-2 text-slate-600 transition hover:bg-slate-100 hover:text-slate-900"
                  title="Editar"
                  aria-label={`Editar destinatário ${contact.nome}`}
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="m4 20 4.2-.8L19 8.4l-3.4-3.4L4.8 15.8 4 20Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
                  </svg>
                </button>
                <button
                  onClick={() => handleDelete(contact.id)}
                  className={`rounded-lg p-2 transition ${deleteConfirm === contact.id ? 'bg-red-100 text-red-700' : 'text-red-600 hover:bg-red-50'}`}
                  title={deleteConfirm === contact.id ? 'Confirmar exclusão' : 'Excluir'}
                  aria-label={deleteConfirm === contact.id ? `Confirmar exclusão de ${contact.nome}` : `Excluir destinatário ${contact.nome}`}
                >
                  <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <path d="M4 7h16M10 11v6m4-6v6M7 7l1 12h8l1-12M9 7l1-2h4l1 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </button>
              </div>
            </div>

            <div className="mt-3 space-y-2 text-xs text-slate-600">
              <div>{renderCategory(contact)}</div>
              <p className="line-clamp-1">{contact.endereco || 'Endereço não informado'}</p>
              <p className="line-clamp-1 text-slate-500">Processo: {contact.processo || '-'} • Mandado: {contact.mandado || '-'}</p>
              <p className="text-slate-500">Atualizado em {formatDate(contact.atualizadoEm)}</p>
            </div>
          </article>
        ))}
      </div>
    </>
  );
}
